'use client';

/* Pricing model — pay-per-successful-outcome, sourced from the General
   Explanation doc. No invented dollar figures (the source redacts them);
   we present the *model* and the basis of each fee. */
import { Icon } from '../icon';
import { IconTile, SectionHead, toneVar, useReveal } from '../primitives';
import { href, type Tone } from '../data';

interface Principle {
  icon: string;
  tone: Tone;
  h: string;
  s: string;
}

interface PriceRow {
  svc: string;
  alias: string;
  when: string;
  basis: string;
  note: string;
  tone: Tone;
}

export function PricingSection() {
  useReveal();
  const principles: Principle[] = [
    { icon: 'circle-check', tone: 'green', h: 'Pay per successful outcome', s: 'Fees apply when a dispute is actually deflected or resolved — not on a subscription. If a service doesn’t stop the dispute, you’re not charged for it.' },
    { icon: 'layers', tone: 'cyan', h: 'Every service is optional', s: 'Switch on only the services you want. They’re built to work in sequence, hand in hand, with no overlap.' },
    { icon: 'trending-down', tone: 'blue', h: 'Lowest total mitigation cost', s: 'Running the full stack resolves disputes as early — and as cheaply — as possible, before expensive chargeback fees ever land.' },
  ];
  const rows: PriceRow[] = [
    { svc: 'PREVENT', alias: 'Order Insight', when: 'A pre-dispute is deflected at inquiry', basis: 'Flat, per outcome', note: 'No refund required', tone: 'cyan' },
    { svc: 'PREVENT+', alias: 'CE3', when: 'Friendly fraud is blocked pre-dispute', basis: 'Variable by transaction value', note: 'No refund required', tone: 'cyan' },
    { svc: 'RDR', alias: 'Rapid Dispute Resolution', when: 'A Visa dispute is resolved (credit issued)', basis: 'Flat · all MCC codes', note: 'Credit issued', tone: 'blue' },
    { svc: 'MC Resolve', alias: 'Mastercard', when: 'A Mastercard dispute is resolved', basis: 'Flat, per outcome', note: 'Credit issued', tone: 'blue' },
    { svc: 'CDRN', alias: 'Multi-brand', when: 'A dispute is resolved across card brands', basis: 'Per outcome', note: 'Refund or 72-hour pass-through', tone: 'blue' },
    { svc: 'INFORM', alias: 'Fraud intelligence', when: 'Fraud & dispute alerts are delivered', basis: 'Per integration', note: 'TC-40 + dispute feed', tone: 'violet' },
  ];
  return (
    <section className="section" id="pricing">
      <div className="container">
        <SectionHead align="center" eyebrow="Pricing model"
          title="You only pay when it works"
          lead="No upfront platform fee. Each service carries a per-outcome price — charged when a dispute is genuinely deflected or resolved on your behalf." />

        <div className="prod-grid reveal" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginTop: 44 }}>
          {principles.map((p, i) => {
            return (
              <div key={i} className="card card-pad" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <IconTile name={p.icon} tone={p.tone} />
                <h3 className="h4" style={{ fontSize: 18 }}>{p.h}</h3>
                <p className="muted pretty" style={{ margin: 0, fontSize: 14, lineHeight: 1.55 }}>{p.s}</p>
              </div>
            );
          })}
        </div>

        <div className="card reveal price-table" style={{ marginTop: 16, overflow: 'hidden' }}>
          <div className="price-row price-head">
            <span>Service</span><span>You pay when…</span><span>Fee basis</span><span>Outcome</span>
          </div>
          {rows.map((r) => {
            const c = toneVar(r.tone);
            return (
              <div key={r.svc} className="price-row" style={{ borderTop: '1px solid var(--line-soft)' }}>
                <span className="row" style={{ gap: 10 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 3, background: c, flexShrink: 0 }} />
                  <span className="stack" style={{ gap: 1 }}>
                    <b style={{ fontSize: 14.5 }}>{r.svc}</b>
                    <span className="subtle mono" style={{ fontSize: 11 }}>{r.alias}</span>
                  </span>
                </span>
                <span className="muted" style={{ fontSize: 14 }}>{r.when}</span>
                <span style={{ fontSize: 14 }}>{r.basis}</span>
                <span><span className="tag" style={{ color: c, borderColor: `color-mix(in oklch, ${c} 36%, transparent)`, background: `color-mix(in oklch, ${c} 10%, transparent)` }}>{r.note}</span></span>
              </div>
            );
          })}
        </div>

        <div className="between wrap reveal" style={{ gap: 16, marginTop: 22 }}>
          <span className="row subtle" style={{ gap: 9, fontSize: 13 }}><Icon name="lock" size={15} style={{ color: 'var(--primary-2)' }} />Per-outcome rates are set with your team during enrollment — combine services for the lowest blended cost.</span>
          <a href={href('enroll')} className="btn btn-primary">Get your rate sheet<Icon name="arrow-right" size={17} /></a>
        </div>
      </div>
    </section>
  );
}
